/**
 * Notification prefs + test-send hooks.
 *
 * The CP owns the source-of-truth prefs row keyed by `parent_id` (see
 * migration 0019). This module is the thin fetch layer the settings
 * panel talks to.
 *
 * The "test" call enqueues one `notification_queue` row per enabled
 * channel with a canned preview and no owning alert. The worker
 * dispatches on its next tick, so a successful test proves the whole
 * pipeline: worker running, Twilio/Resend/WebPush credentials valid,
 * destination reachable. If the parent gets the message, everything
 * downstream works.
 */

import { api } from '../state/api';

export interface NotificationPrefs {
  sms_number: string | null;
  email_address: string | null;
  /** Full JSON push subscription (endpoint + p256dh + auth). */
  push_subscription: unknown | null;
  sms_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
  /** True once the parent has proven control of `sms_number` (028). */
  sms_verified: boolean;
  /**
   * ISO timestamp when the outstanding verification code expires.
   * Null when no code is outstanding — start a new verification to
   * get one. Server hides expired timestamps, so a non-null value
   * always represents a live challenge.
   */
  sms_verification_expires_at: string | null;
  updated_at: string;
}

export interface UpdateNotificationPrefs {
  sms_number: string | null;
  email_address: string | null;
  push_subscription: unknown | null;
  sms_enabled: boolean;
  email_enabled: boolean;
  push_enabled: boolean;
}

export interface TestResult {
  enqueued: string[];
}

export async function getNotificationPrefs(): Promise<NotificationPrefs> {
  return api<NotificationPrefs>('/v1/family/parent/notification-prefs');
}

export async function saveNotificationPrefs(
  prefs: UpdateNotificationPrefs,
): Promise<void> {
  await api('/v1/family/parent/notification-prefs', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

/**
 * Fire a test notification on every currently-enabled channel. Returns
 * the list of channels the CP enqueued for — delivery success/failure
 * lands asynchronously (the parent's SMS/email inbox is the ground
 * truth).
 */
export async function sendTestNotification(): Promise<TestResult> {
  return api<TestResult>('/v1/family/notifications/test', { method: 'POST' });
}

/**
 * Start SMS verification: server generates a 6-digit code, stores its
 * hash + 10-min expiry, and enqueues an SMS carrying the plaintext.
 * PWA then prompts the parent for the code. Returns the expiry so we
 * can render a countdown.
 */
export interface VerifyStartResult {
  sent: boolean;
  expires_at: string;
}
export async function verifySmsStart(
  smsNumber: string,
): Promise<VerifyStartResult> {
  return api<VerifyStartResult>(
    '/v1/family/parent/notification-prefs/sms/verify-start',
    {
      method: 'POST',
      body: JSON.stringify({ sms_number: smsNumber }),
    },
  );
}

export interface VerifyConfirmResult {
  verified: boolean;
}
/**
 * Submit the code the parent typed. Server hashes + constant-time
 * compares against the stored hash. Match → `verified: true` and the
 * prefs row's `sms_verified` flips on. Mismatch throws with a 400.
 */
export async function verifySmsConfirm(
  code: string,
): Promise<VerifyConfirmResult> {
  return api<VerifyConfirmResult>(
    '/v1/family/parent/notification-prefs/sms/verify-confirm',
    {
      method: 'POST',
      body: JSON.stringify({ code }),
    },
  );
}
